import React, { useState } from 'react'
import { Row, Col, Pagination, Modal, Tabs, Tab } from 'react-bootstrap'
import { Sliders, BarChart2 } from 'lucide-react'
import styled from 'styled-components'
import MiniClassDistributionCard from '../../../components/MiniClassDistributionCard'

interface ModelDatasetViewHeaderProps {
  imagesPerRow: number
  onImagesPerRowChange?: (size: number) => void
  currentPage: number
  totalImages: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  activeTab: 'training' | 'validation'
  onTabChange: (tab: 'training' | 'validation') => void
  datasetId: string
}

const SizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  background: #2a2a2a;
  border-radius: 8px;
  justify-self: end;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #fff;
    font-size: 0.9rem;
  }
`

const RangeInput = styled.input`
  width: 150px;
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #2a2a2a;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  justify-self: center;
  z-index: 1;

  .pagination {
    margin: 0;
    position: relative;
    z-index: 1;
  }

  .page-link {
    background-color: #2a2a2a;
    border-color: #3a3a3a;
    color: #fff;
    padding: 0.35rem 0.6rem;
    transition: all 0.2s ease;
    font-size: 0.85rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    position: relative;
    z-index: 1;

    &:hover {
      background-color: #3a3a3a;
      border-color: #4a4a4a;
    }
  }

  .page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: #fff;
  }

  .page-item.disabled .page-link {
    background-color: #222;
    border-color: #333;
    color: #666;
  }
`

const PaginationInfo = styled.div`
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-weight: 500;
`

const IconButton = styled.button`
  background: #2a2a2a;
  border: none;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-right: 1rem;

  &:hover {
    background: #3a3a3a;
  }

  svg {
    color: #fff;
  }
`

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    border-radius: 12px;
    color: #fff;
  }

  .modal-header {
    background: #2a2a2a;
    border-bottom: 1px solid #3a3a3a;
    border-radius: 12px 12px 0 0;
    padding: 1rem;

    .modal-title {
      color: #fff;
      font-size: 1.1rem;
      font-weight: 500;
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: #1a1a1a;
    border-radius: 0 0 12px 12px;
    padding: 1.5rem;
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 0;
  margin-right: 1rem;

  .nav-item {
    margin-right: 0.5rem;
  }

  .nav-link {
    background: #2a2a2a;
    border: none;
    color: #a0a0a0;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    font-size: 0.9rem;

    &:hover {
      background: #3a3a3a;
      color: #fff;
    }

    &.active {
      background: #4a4a4a;
      color: #fff;
      font-weight: 500;
    }
  }
`

const ModelDatasetViewHeader: React.FC<ModelDatasetViewHeaderProps> = ({
  imagesPerRow,
  onImagesPerRowChange,
  currentPage,
  totalImages,
  itemsPerPage,
  onPageChange,
  activeTab,
  onTabChange,
  datasetId,
}) => {
  const [showDistributionModal, setShowDistributionModal] = useState(false)
  const totalPages = Math.ceil(totalImages / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalImages)

  return (
    <Row className="mb-4 align-items-center" style={{ width: '100%', padding: '0 1rem' }}>
      <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
        <StyledTabs
          activeKey={activeTab}
          onSelect={(k) => onTabChange(k as 'training' | 'validation')}
          id="dataset-tabs"
        >
          <Tab eventKey="training" title="Training Data" />
          <Tab eventKey="validation" title="Validation Data" />
        </StyledTabs>
      </Col>
      <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
        <PaginationContainer>
          <Pagination>
            <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            <Pagination.Item active>{currentPage}</Pagination.Item>
            <Pagination.Next
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
          <PaginationInfo>
            {startItem}-{endItem} of {totalImages} images
          </PaginationInfo>
        </PaginationContainer>
      </Col>
      <Col
        xs={4}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: '0',
        }}
      >
        <IconButton
          onClick={() => setShowDistributionModal(true)}
          style={{ backgroundColor: '#800085', width: '4rem' }}
        >
          <BarChart2 color="#000" size={24} />
        </IconButton>
        {onImagesPerRowChange && (
          <SizeControl>
            <label>
              <Sliders size={16} />
              Size
            </label>
            <RangeInput
              type="range"
              min="1"
              max="6"
              value={7 - imagesPerRow}
              onChange={(e) =>
                onImagesPerRowChange && onImagesPerRowChange(7 - parseInt(e.target.value))
              }
              className="form-range"
            />
          </SizeControl>
        )}
      </Col>

      <StyledModal
        show={showDistributionModal}
        onHide={() => setShowDistributionModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Class Distribution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MiniClassDistributionCard showButtons={false} datasetId={datasetId} />
        </Modal.Body>
      </StyledModal>
    </Row>
  )
}

export default ModelDatasetViewHeader
